import { ChampionshipDriver, Incident, Infraction, StatusIncident } from "api";
import { BroadcastMessage } from "api/model/Race";
import { DateTime } from "luxon";

export enum PenaltyType {
    ADD_TIME_TO_END = "ADD_TIME_TO_END",
    DISABLE_CHAT = "DISABLE_CHAT",
    DISQUALIFY = "DISQUALIFY",
    DRIVE_THROUGH = "DRIVE_THROUGH",
    REMOVE = "REMOVE",
    STOP_AND_GO = "STOP_AND_GO",
    WARNING = "WARNING",
}

export const allPenaltiesTypes = [
    PenaltyType.ADD_TIME_TO_END,
    PenaltyType.DISABLE_CHAT,
    PenaltyType.DRIVE_THROUGH,
    PenaltyType.STOP_AND_GO,
    PenaltyType.DISQUALIFY,
    PenaltyType.REMOVE,
    PenaltyType.WARNING,
];

const PenaltyDescriptionByType = {};
PenaltyDescriptionByType[PenaltyType.ADD_TIME_TO_END] =
    "Acréscimo de tempo ao fim da prova";
PenaltyDescriptionByType[PenaltyType.DISABLE_CHAT] = "Chat desabilitado";
PenaltyDescriptionByType[PenaltyType.DRIVE_THROUGH] = "Drive through";
PenaltyDescriptionByType[PenaltyType.STOP_AND_GO] = "Stop and go";
PenaltyDescriptionByType[PenaltyType.DISQUALIFY] = "Desclassificado";
PenaltyDescriptionByType[PenaltyType.REMOVE] = "Removido do servidor";
PenaltyDescriptionByType[PenaltyType.WARNING] = "Advertência";

export { PenaltyDescriptionByType };
export { PenaltyCommandByType };

const disableChatCommand = (carNumber: number) => `!nchat #${carNumber}`;

const driveThroughCommand = (carNumber: number, message?: string) =>
    `!black #${carNumber} D - ${message || ""}`;

const stopAndGoCommand = (
    carNumber: number,
    seconds: number,
    message?: string
) => `!black #${carNumber} ${seconds} - ${message || ""}`;

const disqualifyCommand = (carNumber: number, message?: string) =>
    `!dq #${carNumber} - ${message || ""}`;

const removeCommand = (carNumber: number, message?: string) =>
    `!remove #${carNumber} - ${message || ""}`;

const PenaltyCommandByType = {
    DISABLE_CHAT: disableChatCommand,
    DRIVE_THROUGH: driveThroughCommand,
    STOP_AND_GO: stopAndGoCommand,
    DISQUALIFY: disqualifyCommand,
    REMOVE: removeCommand,
};

interface Penalty {
    type: PenaltyType;
    command?:
        | ((carNumber: number) => string)
        | ((carNumber: number, message?: string) => string)
        | ((carNumber: number, seconds: number, message?: string) => string);
    description: string;
}

export const createPenaltyByType = (type: PenaltyType): Penalty => ({
    type,
    command: PenaltyCommandByType[type],
    description: PenaltyDescriptionByType[type],
});

export const createMessageToBroadcast = (
    incident: Incident,
    roundId: string,
    driversByNumber,
    infraction?: Infraction
): BroadcastMessage => {
    const amountInvolvedCars = incident.involvedCars.filter((carNumber) =>
        incident.status === StatusIncident.APPLIED
            ? incident.guiltyCar !== carNumber
            : true
    ).length;
    const involvedCars = incident.involvedCars
        .filter((carNumber) =>
            incident.status === StatusIncident.APPLIED
                ? incident.guiltyCar !== carNumber
                : true
        )
        .map((carNumber) => {
            const driver: ChampionshipDriver = driversByNumber[carNumber];
            return `#${carNumber} - ${driver.firstName.charAt(0)}.${
                driver.lastName
            }`;
        })
        .join(", ");

    let message = "";

    if (incident.status === StatusIncident.INVESTIGATING) {
        message = `Incidente na volta ${incident.lap} envolvendo os carros (${involvedCars}) está sob investigação`;
    }
    if (incident.status === StatusIncident.AFTER_RACE) {
        message = `Incidente na volta ${incident.lap} envolvendo os carros (${involvedCars}) será analisado após a corrida`;
    }
    if (incident.status === StatusIncident.NO_FURTHER_ACTION) {
        message = `Incidente na volta ${incident.lap} envolvendo os carros (${involvedCars}) nenhuma ação necessária`;
    }

    if (infraction && incident.status === StatusIncident.APPLIED) {
        const guiltyDriver: ChampionshipDriver =
            driversByNumber[incident.guiltyCar || ""];
        const guiltyDriverDisplay = `#${
            incident.guiltyCar
        } (${guiltyDriver.firstName.charAt(0)}.${guiltyDriver.lastName})`;

        const incidentWithCarsMessage = `por incidente na volta ${
            incident.lap
        } com o${amountInvolvedCars > 1 ? "s" : ""} carro${
            amountInvolvedCars > 1 ? "s" : ""
        } (${involvedCars})`;

        if (infraction.penaltyType === PenaltyType.DISQUALIFY) {
            message = `Carro ${guiltyDriverDisplay} desclassificado da etapa ${incidentWithCarsMessage}`;
        }
        if (infraction.penaltyType === PenaltyType.DRIVE_THROUGH) {
            message = `Drive through para o carro ${guiltyDriverDisplay} ${incidentWithCarsMessage}`;
        }
        if (infraction.penaltyType === PenaltyType.REMOVE) {
            message = `Carro ${guiltyDriverDisplay} removido do servidor por: ${infraction.description}`;
        }
        if (infraction.penaltyType === PenaltyType.STOP_AND_GO) {
            message = `Stop and go para o carro ${guiltyDriverDisplay} ${incidentWithCarsMessage}`;
        }
        if (infraction.penaltyType === PenaltyType.WARNING) {
            message = `Advertência para o carro ${guiltyDriverDisplay} ${incidentWithCarsMessage}`;
        }
        if (infraction.penaltyType === PenaltyType.DISABLE_CHAT) {
            message = `Chat desabilitado para o carro ${guiltyDriverDisplay}`;
        }
        if (infraction.penaltyType === PenaltyType.ADD_TIME_TO_END) {
            message = `Será adicionado ${infraction.addTimeToEnd} segundos para o carro ${guiltyDriverDisplay} ${incidentWithCarsMessage}`;
        }
    }

    return {
        message,
        transmitted: false,
        raceControlId: incident.raceControlId,
        roundId,
        statusIncident: incident.status,
        incidentId: incident.id,
        createdAt: DateTime.local().toString(),
    };
};
